"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, User, Phone } from "lucide-react";

export default function LeadPopup() {
  const pathname = usePathname();

  // Don't show the lead popup on location pages (they interfere with navigation)
  if (pathname && pathname.startsWith("/free-zone/locations")) return null;

  const [isOpen, setIsOpen] = useState(false);
  const [hasSeenPopup, setHasSeenPopup] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  useEffect(() => {
    // Check if user has already seen the popup in this session
    const seen = sessionStorage.getItem("hasSeenLeadPopup");
    if (seen) {
      setHasSeenPopup(true);
      return;
    }

    // Show popup shortly after they arrive
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const closePopup = () => {
    setIsOpen(false);
    sessionStorage.setItem("hasSeenLeadPopup", "true");
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const response = await fetch("https://formspree.io/f/xbdnyapg", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, source: "Popup" }),
    });
    if (response.ok) {
      setSubmitted(true);
      setTimeout(() => {
        closePopup();
        setSubmitted(false);
        setForm({ name: "", email: "", phone: "" });
      }, 2500);
    }
  };

  if (hasSeenPopup && !isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#D4A754]/30 shadow-2xl"
            style={{
              background: "rgba(13, 12, 9, 0.95)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 30px rgba(212, 167, 84, 0.15)",
            }}
          >
            {/* Close Button */}
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 z-10 p-2 text-white/50 transition-colors hover:text-white"
            >
              <X size={20} />
            </button>

            <div className="p-8">
              <div className="mb-6 text-center">
                <h2
                  className="mb-2 text-2xl font-black tracking-widest uppercase"
                  style={{ color: "#ffffff", fontFamily: "var(--font-montserrat)" }}
                >
                  Unlock Your Growth
                </h2>
                <p className="text-sm text-white/70">
                  Ready to elevate your business? Leave your details below and our experts will contact you shortly.
                </p>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                {submitted ? (
                  <div className="text-center py-6">
                    <div className="text-4xl mb-3">✓</div>
                    <p className="text-white font-bold text-lg">Message Sent!</p>
                    <p className="text-white/60 text-sm mt-1">Our team will contact you shortly.</p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <User size={18} className="text-[#D4A754]/70" />
                      </div>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Full Name"
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#D4A754] focus:ring-1 focus:ring-[#D4A754] transition-all"
                      />
                    </div>

                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Mail size={18} className="text-[#D4A754]/70" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="Email Address"
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#D4A754] focus:ring-1 focus:ring-[#D4A754] transition-all"
                      />
                    </div>

                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Phone size={18} className="text-[#D4A754]/70" />
                      </div>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#D4A754] focus:ring-1 focus:ring-[#D4A754] transition-all"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full mt-6 py-3 rounded-xl font-bold text-sm tracking-[0.2em] uppercase transition-all duration-300 active:scale-95"
                      style={{
                        background: "linear-gradient(135deg, #B8893A 0%, #D4A754 60%, #f0c97a 100%)",
                        color: "#0D0C09",
                        boxShadow: "0 8px 30px rgba(184,137,58,0.35), 0 2px 8px rgba(0,0,0,0.3)",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 12px 40px rgba(184,137,58,0.55), 0 2px 8px rgba(0,0,0,0.3)")}
                      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 8px 30px rgba(184,137,58,0.35), 0 2px 8px rgba(0,0,0,0.3)")}
                    >
                      Get Started Now
                    </button>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
